/**
 * 牛牛游戏音效管理模块
 * 根据语言动态加载中文/英文音效
 */

import { Howl } from 'howler';
import useSettingsStore from '~/stores/settings';

// 中文音效
import cnStartBet from '../mp3/cn/start_bet.mp3';
import cnStopBet from '../mp3/cn/stop_bet.mp3';
import cnCountdownWarning from '../mp3/cn/countdown_warning.mp3';
import cnCountdownTick from '../mp3/cn/countdown_tick.mp3';
import cnBankerWinsAll from '../mp3/cn/banker_wins_all.mp3';
import cnBankerLosesAll from '../mp3/cn/banker_loses_all.mp3';
import cnNoBull1 from '../mp3/cn/no_bull.mp3';
import cnBull1 from '../mp3/cn/bull_1.mp3';
import cnBull2 from '../mp3/cn/bull_2.mp3';
import cnBull3 from '../mp3/cn/bull_3.mp3';
import cnBull4 from '../mp3/cn/bull_4.mp3';
import cnBull5 from '../mp3/cn/bull_5.mp3';
import cnBull6 from '../mp3/cn/bull_6.mp3';
import cnBull7 from '../mp3/cn/bull_7.mp3';
import cnBull8 from '../mp3/cn/bull_8.mp3';
import cnBull9 from '../mp3/cn/bull_9.mp3';
import cnBullBull from '../mp3/cn/bull_bull.mp3';

// 英文音效
import enStartBet from '../mp3/en/start_bet.mp3';
import enStopBet from '../mp3/en/stop_bet.mp3';
import enBankerWinsAll from '../mp3/en/banker_wins_all.mp3';
import enBankerLosesAll from '../mp3/en/banker_loses_all.mp3';
import enNoBull from '../mp3/en/no_bull.mp3';
import enBull1 from '../mp3/en/bull_1.mp3';
import enBull2 from '../mp3/en/bull_2.mp3';
import enBull3 from '../mp3/en/bull_3.mp3';
import enBull4 from '../mp3/en/bull_4.mp3';
import enBull5 from '../mp3/en/bull_5.mp3';
import enBull6 from '../mp3/en/bull_6.mp3';
import enBull7 from '../mp3/en/bull_7.mp3';
import enBull8 from '../mp3/en/bull_8.mp3';
import enBull9 from '../mp3/en/bull_9.mp3';
import enBullBull from '../mp3/en/bull_bull.mp3';

// 音效映射
const audioMap = {
  cn: {
    startBet: cnStartBet,
    stopBet: cnStopBet,
    countdownWarning: cnCountdownWarning,
    countdownTick: cnCountdownTick,
    bankerWinsAll: cnBankerWinsAll,
    bankerLosesAll: cnBankerLosesAll,
    noBull: cnNoBull1, 
    bull1: cnBull1,
    bull2: cnBull2,
    bull3: cnBull3,
    bull4: cnBull4,
    bull5: cnBull5,
    bull6: cnBull6,
    bull7: cnBull7,
    bull8: cnBull8,
    bull9: cnBull9,
    bullBull: cnBullBull,
  },
  en: {
    startBet: enStartBet,
    stopBet: enStopBet,
    countdownWarning: cnCountdownWarning, 
    countdownTick: cnCountdownTick, 
    bankerWinsAll: enBankerWinsAll,
    bankerLosesAll: enBankerLosesAll,
    noBull: enNoBull,
    bull1: enBull1,
    bull2: enBull2,
    bull3: enBull3,
    bull4: enBull4,
    bull5: enBull5,
    bull6: enBull6,
    bull7: enBull7,
    bull8: enBull8,
    bull9: enBull9,
    bullBull: enBullBull,
  }
};

export type AudioKey = keyof typeof audioMap.cn;

interface GameAudioInstance {
  play: (key: AudioKey) => void;
  playBullType: (niuType: number) => void;
  playResult: (winningSectorsCount: number) => void;
  stop: (key: AudioKey) => void;
  stopAll: () => void;
  destroy: () => void;
  setLocale: (locale: string) => void;
}

/**
 * 创建牛牛游戏音效管理器
 * @param initialLocale 初始语言 'cn' | 'en'
 */
export function createGameAudio(initialLocale: string = 'en'): GameAudioInstance {
  const settingStore = useSettingsStore();
  const { sound } = settingStore.inhouseGames;
  
  let currentLocale: 'cn' | 'en' = initialLocale === 'cn' ? 'cn' : 'en';
  const audioInstances: Map<string, Howl> = new Map();

  // 获取或创建音效实例
  const getAudio = (key: AudioKey): Howl => {
    const cacheKey = `${currentLocale}_${key}`;
    
    if (!audioInstances.has(cacheKey)) {
      const src = audioMap[currentLocale][key];
      const howl = new Howl({
        src: [src],
        html5: true,
        volume: sound.effects,
        preload: true,
      });
      audioInstances.set(cacheKey, howl);
    }
    
    return audioInstances.get(cacheKey)!;
  };

  // 播放音效
  const play = (key: AudioKey) => {
    if (!sound.on) return;
    
    try {
      const audio = getAudio(key);
      audio.volume(sound.effects);
      audio.play();
    } catch (e) {
      console.warn(`[GameAudio] 播放失败: ${key}`, e);
    }
  };

  // 🔧 牛型音效管理（立即替换模式，确保与UI同步）
  const bullTypeKeys: AudioKey[] = ['noBull', 'bull1', 'bull2', 'bull3', 'bull4', 'bull5', 'bull6', 'bull7', 'bull8', 'bull9', 'bullBull'];
  
  // 停止所有牛型音效
  const stopAllBullTypes = () => {
    bullTypeKeys.forEach(k => {
      const cacheKey = `${currentLocale}_${k}`;
      const audio = audioInstances.get(cacheKey);
      if (audio) {
        audio.stop();
      }
    });
  };

  // 根据牛型播放对应音效（立即替换模式）
  const playBullType = (niuType: number) => {
    if (!sound.on) return;
    
    const keyMap: Record<number, AudioKey> = {
      0: 'noBull',
      1: 'bull1',
      2: 'bull2',
      3: 'bull3',
      4: 'bull4',
      5: 'bull5',
      6: 'bull6',
      7: 'bull7',
      8: 'bull8',
      9: 'bull9',
      10: 'bullBull',
    };
    
    const key = keyMap[niuType];
    if (key) {
      try {
        // 🔧 立即停止当前播放的牛型音效，然后播放新的
        stopAllBullTypes();
        
        const audio = getAudio(key);
        audio.volume(sound.effects);
        audio.play();
      } catch (e) {
        console.warn(`[GameAudio] 播放牛型音效失败: ${key}`, e);
      }
    }
  };

  // 根据获胜区域数量播放通吃/通赔音效
  const playResult = (winningSectorsCount: number) => {
    if (!sound.on) return;
    
    if (winningSectorsCount === 0) {
      // 庄家通吃（没有获胜区域）
      play('bankerWinsAll');
    } else if (winningSectorsCount === 4) {
      // 庄家通赔（所有区域都获胜）
      play('bankerLosesAll');
    }
  };

  // 停止指定音效
  const stop = (key: AudioKey) => {
    const cacheKey = `${currentLocale}_${key}`;
    const audio = audioInstances.get(cacheKey);
    if (audio) {
      audio.stop();
    }
  };

  // 停止所有音效
  const stopAll = () => {
    audioInstances.forEach(audio => audio.stop());
  };

  // 销毁所有音效实例
  const destroy = () => {
    audioInstances.forEach(audio => audio.unload());
    audioInstances.clear();
  };

  // 切换语言
  const setLocale = (locale: string) => {
    currentLocale = locale === 'cn' ? 'cn' : 'en';
  };

  return {
    play,
    playBullType,
    playResult,
    stop,
    stopAll,
    destroy,
    setLocale,
  };
}

// 导出类型
export type { GameAudioInstance };
